/**
 * strategyHorizon.ts — does this strategy have the time it needs?
 *
 * WHY THIS EXISTS (owner, 23 Sep 2026). The fact-find asked for a health status, told the client it
 * was "used only to model care costs and government support", and used it for neither: no calculation
 * read it. Meanwhile the Suggestion Engine ranked every candidate over the same five years with the
 * same confidence for an 89-year-old as for a 70-year-old, so a nil-residual lifetime annuity — a bet
 * that pays off only if the client lives many years — could be crowned "best cashflow" for the very
 * old, precisely because the provider prices mortality in and pays them a high rate.
 *
 * WHAT IT IS. One rule, read by every surface (the engine's cards and its leader picking, Manual
 * Control's warnings, the advice document), so no screen can say something different:
 *
 *   1. The client's AGE sets a base horizon band from the life expectancy table already used to price
 *      annuities — long (more than HORIZON_LONG_YEARS), medium, or short (under HORIZON_MEDIUM_YEARS).
 *   2. A self-reported POOR health answer moves the band one step shorter. Good, fair and blank leave
 *      it alone: fair is too coarse a judgement to act on, and blank must never become a silent
 *      assumption either way.
 *   3. Each strategy declares the horizon it NEEDS. Where it needs more than the band supports, a
 *      warning is produced in plain words.
 *
 * WHAT IT IS NOT. It moves no figure: nothing here touches a projection, a ranking metric or a fee.
 * It is a suitability judgement layered on top of the arithmetic, and it never puts a life expectancy
 * on a screen — the bands are the only thing that leaves this file.
 *
 * WHOSE HORIZON. The annuity's owner (a joint annuity uses the joint expectancy); for a home sold to
 * buy a smaller one, the partner who would live in it, since in this module the client is in care.
 * Those choices are made by the callers, which know the plan; this file only knows a person.
 */
import { getLifeExpectancy, sexOf } from './annuityStrategies.ts';

export type HorizonBand = 'long' | 'medium' | 'short';
export type HorizonNeed = 'long' | 'medium' | 'none';

/** More than this many years of expected life is a LONG horizon. Owner-set, 23 Sep 2026. */
export const HORIZON_LONG_YEARS = 10;
/** Under this many years is a SHORT horizon; between the two is MEDIUM. Owner-set, 23 Sep 2026. */
export const HORIZON_MEDIUM_YEARS = 5;

/**
 * A lifetime annuity returning less than this share of its price to the estate is treated as a
 * nil-residual annuity for horizon purposes: the estate is betting most of the capital on longevity.
 */
export const RESIDUAL_SHARE_FOR_MEDIUM_HORIZON = 0.5;

const ORDER: Record<HorizonBand, number> = { short: 0, medium: 1, long: 2 };

const normaliseHealth = (status: unknown): 'good' | 'fair' | 'poor' | null => {
    const s = String(status ?? '').trim().toLowerCase();
    return s === 'good' || s === 'fair' || s === 'poor' ? s : null;
};

/** The band the life expectancy alone implies. */
export function horizonBandForYears(years: number): HorizonBand {
    if (years > HORIZON_LONG_YEARS) return 'long';
    if (years >= HORIZON_MEDIUM_YEARS) return 'medium';
    return 'short';
}

/**
 * The horizon a person can be planned on: their age's band, one step shorter where their health is
 * recorded as poor. Null where the age is not known — a missing date of birth must produce no
 * judgement, not a default one.
 */
export function horizonBandForPerson(input: {
    age: number | null | undefined;
    healthStatus?: unknown;
    /** A joint-life annuity is planned on the joint expectancy; pass the partner's age for that. */
    partnerAge?: number | null;
    /** Sex, as the Government Actuary's tables are drawn; unknown reads as the shorter table. */
    sex?: unknown;
    partnerSex?: unknown;
}): HorizonBand | null {
    const age = typeof input.age === 'number' && Number.isFinite(input.age) ? input.age : null;
    if (age == null) return null;
    const joint = typeof input.partnerAge === 'number' && Number.isFinite(input.partnerAge);
    const years = getLifeExpectancy({ age, partnerAge: joint ? (input.partnerAge as number) : null, type: joint ? 'joint' : 'single', sex: sexOf(input.sex), partnerSex: sexOf(input.partnerSex) });
    const base = horizonBandForYears(years);
    if (normaliseHealth(input.healthStatus) !== 'poor') return base;
    return base === 'long' ? 'medium' : 'short';
}

/**
 * What a lifetime annuity needs. `residualCapital` is the share of the price returned to the estate
 * (0 = nil residual, 1 = capital returned), as the engine and Manual Control both record it.
 */
export function horizonNeededForLifetimeAnnuity(residualCapital: unknown): HorizonNeed {
    const share = typeof residualCapital === 'number' ? residualCapital : parseFloat(String(residualCapital ?? '0'));
    const r = Number.isFinite(share) ? share : 0;
    return r >= RESIDUAL_SHARE_FOR_MEDIUM_HORIZON ? 'medium' : 'long';
}

/**
 * What selling the home needs. Only a sale that BUYS a smaller home has sunk costs (stamp duty, the
 * move); a home sold outright to fund the deposit is sold either way, now or by the estate later, and
 * the deposit comes back — so that sale needs no horizon at all.
 */
export function horizonNeededForHomeSale(input: { buysNewHome: boolean }): HorizonNeed {
    return input.buysNewHome ? 'medium' : 'none';
}

/** True where the strategy needs more time than the person's band supports. */
export function horizonConflicts(need: HorizonNeed, band: HorizonBand | null): boolean {
    if (need === 'none' || band == null) return false;
    return ORDER[band] < ORDER[need === 'long' ? 'long' : 'medium'];
}

/**
 * The warning, in the words a client reads. No number about the person, deliberately: the bands
 * are the only thing that leaves this rule.
 */
export function horizonWarning(input: {
    need: HorizonNeed;
    band: HorizonBand | null;
    /** The person whose horizon is being judged, by first name. */
    personName: string;
    /** "a lifetime annuity with nil residual", "selling the home to buy a smaller one" … */
    strategyLabel: string;
    /** Whether that person's recorded health contributed to the band. */
    healthConsidered?: boolean;
    /**
     * 'capital_guaranteed' for the annuity-plus-life-policy shape, where the full purchase price comes
     * back on death: a short stay loses little, so the warning says there is little to GAIN rather
     * than that it may not suit (owner, 9 Oct 2026: "reads a bit contradictory").
     */
    shape?: 'capital_guaranteed';
}): string | null {
    if (!horizonConflicts(input.need, input.band)) return null;
    const needs = input.need === 'long' ? 'many years' : 'several years';
    const basis = input.healthConsidered ? `${input.personName}'s age and recorded health` : `${input.personName}'s age`;
    if (input.shape === 'capital_guaranteed') {
        return `${capitalise(input.strategyLabel)} pays the full amount back on death, so a short stay loses little. Its pension and fee savings build over ${needs}, though, and ${basis} point to a shorter time frame, so there is little for ${input.personName} to gain from it.`;
    }
    return `${capitalise(input.strategyLabel)} usually needs ${needs} to pay for itself. ${basis} point to a shorter time frame, so it may not suit ${input.personName}.`;
}

const capitalise = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/**
 * The years a person can be planned on, as a number: their life expectancy (single, or the longer of
 * two for a couple), held to the band below where their health is recorded as poor — the same
 * step-down the band rule applies, said in years. Null where the age is not known.
 *
 * Read by the aged care cashflow reassurance (owner, 6 Oct 2026): a shortfall met from savings is
 * "long enough" when the savings last at least this long.
 */
export function horizonYearsForPerson(input: {
    age: number | null | undefined;
    healthStatus?: unknown;
    partnerAge?: number | null;
    sex?: unknown;
    partnerSex?: unknown;
}): number | null {
    const age = typeof input.age === 'number' && Number.isFinite(input.age) ? input.age : null;
    if (age == null) return null;
    const joint = typeof input.partnerAge === 'number' && Number.isFinite(input.partnerAge);
    const years = getLifeExpectancy({ age, partnerAge: joint ? (input.partnerAge as number) : null, type: joint ? 'joint' : 'single', sex: sexOf(input.sex), partnerSex: sexOf(input.partnerSex) });
    if (normaliseHealth(input.healthStatus) !== 'poor') return Math.round(years);
    const band = horizonBandForYears(years);
    const capped = band === 'long' ? Math.min(years, HORIZON_LONG_YEARS) : band === 'medium' ? Math.min(years, HORIZON_MEDIUM_YEARS) : years;
    return Math.round(capped);
}
